import { landingApiRequest } from '@/lib/landing-api'
import { ApiClientError } from '@/lib/api/client'
import { validateScratchRegisterForm } from '@/lib/person-data/validate-register-fields'
import type { AvatarConfig } from '@/features/donorAvatar/avatar-catalog'
import type { MascotConfig } from '@/features/donorMascot/mascot-catalog'

export type LandingDonorAddress = {
  id?: number | null
  postal_code: string | null
  state: string | null
  city: string | null
  street: string | null
  neighborhood: string | null
  number: string | null
  complement: string | null
}

export type LandingDonor = {
  id: number
  donor_id?: number
  institution_id: number | null
  is_guest: boolean
  person_type: 'pf' | 'pj' | null
  corporate_name: string | null
  full_name: string | null
  name: string | null
  email: string | null
  phone: string | null
  document_cpf: string | null
  document_cnpj: string | null
  gender: string | null
  birth_date: string | null
  website: string | null
  logo_url: string | null
  profile_photo_url: string | null
  avatar?: AvatarConfig | null
  mascot?: MascotConfig | null
  address: LandingDonorAddress | null
}

export type LandingDonorLoginInput = {
  institution_id?: number
  email: string
  password: string
}

export type LandingDonorPhoneLoginInput = {
  institution_id?: number
  phone: string
  password: string
}

export type LandingDonorEmailAccessInput = {
  institution_id?: number
  email: string
}

export type LandingDonorPhoneAccessInput = {
  institution_id?: number
  phone: string
}

export type LandingDonorAccessStatus =
  | 'password_required'
  | 'password_creation_required'
  | 'registration_required'

export type LandingDonorRegisterInput = {
  institution_id?: number
  person_type: 'pf'
  full_name: string
  document_cpf: string
  email: string
  phone: string
  password?: string
}

export type LandingDonorProfileUpdateInput = {
  person_type: 'pf' | 'pj'
  full_name?: string | null
  corporate_name?: string | null
  document_cpf?: string | null
  document_cnpj?: string | null
  email?: string | null
  phone?: string | null
  gender?: string | null
  birth_date?: string | null
  website?: string | null
  address?: Partial<LandingDonorAddress> | null
}

export type LandingDonorPasswordChangeInput = {
  current_password: string
  new_password: string
  new_password_confirmation: string
}

export type LandingLuckyNumber = {
  is_won?: boolean
  roulette_prize_id?: number | null
  prize_source?:
    | 'roulette'
    | 'scratch_card'
    | 'purchase_scratch_card'
    | 'achievement'
    | null
  formatted_number: string
  assigned_at: string | null
  purchase_id?: number | null
  purchase_at?: string | null
  purchase_amount?: string | null
}

type DonorAuthResponse = {
  donor: LandingDonor
  token?: string
  registration_mode?: 'created' | 'completed'
}

type DonorEmailAccessResponse = {
  status: LandingDonorAccessStatus
  email: string
  has_account: boolean
  has_password: boolean
}

type DonorPhoneAccessResponse = {
  status: LandingDonorAccessStatus
  phone: string
  has_account: boolean
  has_password: boolean
}

type DonorMeResponse = {
  donor: LandingDonor
}

type DonorProfileResponse = {
  donor: LandingDonor
}

type CampaignLuckyNumbersResponse = {
  campaign_id: number
  numbers: LandingLuckyNumber[]
}

type ChangePasswordResponse = {
  message: string
}

type DefinePasswordResponse = {
  token?: string
  user: {
    id: number
    type: string
    name: string
  }
}

export async function landingCheckDonorEmailAccess(
  input: LandingDonorEmailAccessInput
): Promise<DonorEmailAccessResponse> {
  return landingApiRequest<DonorEmailAccessResponse>('/api/donor/email-access/check', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function landingCheckDonorPhoneAccess(
  input: LandingDonorPhoneAccessInput
): Promise<DonorPhoneAccessResponse> {
  return landingApiRequest<DonorPhoneAccessResponse>('/api/donor/phone-access/check', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function landingDonorLogin(
  input: LandingDonorLoginInput
): Promise<DonorAuthResponse> {
  return landingApiRequest<DonorAuthResponse>('/api/donor/login', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function landingDonorLoginByPhone(
  input: LandingDonorPhoneLoginInput,
  campaignAccount = false
): Promise<DonorAuthResponse> {
  return landingApiRequest<DonorAuthResponse>(
    campaignAccount
      ? '/api/donor/campaign-account/phone-access/login'
      : '/api/donor/phone-access/login',
    {
      method: 'POST',
      body: JSON.stringify(input),
    }
  )
}

export async function landingDonorRegister(
  input: LandingDonorRegisterInput,
  campaignAccount = false
): Promise<DonorAuthResponse> {
  const errors = validateScratchRegisterForm({
    name: input.full_name,
    email: input.email,
    phone: input.phone,
    document: input.document_cpf,
  }).filter((error) => error.field !== 'document' || input.document_cpf !== '')
  if (errors.length) {
    throw new ApiClientError(
      errors[0].message,
      422,
      Object.fromEntries(
        errors.map((error) => [
          error.field === 'document'
            ? 'document_cpf'
            : error.field === 'name'
              ? 'full_name'
              : error.field,
          [error.message],
        ])
      )
    )
  }
  return landingApiRequest<DonorAuthResponse>(
    campaignAccount ? '/api/donor/campaign-account/register' : '/api/donor/register',
    {
      method: 'POST',
      body: JSON.stringify(input),
    }
  )
}

export async function landingDefineDonorPasswordByEmail(
  input: LandingDonorEmailAccessInput & {
    password: string
    password_confirmation: string
  }
): Promise<DefinePasswordResponse> {
  return landingApiRequest<DefinePasswordResponse>(
    '/api/donor/email-access/define-password',
    {
      method: 'POST',
      body: JSON.stringify(input),
    }
  )
}

export async function landingDefineDonorPasswordByPhone(
  input: LandingDonorPhoneAccessInput & {
    password: string
    password_confirmation: string
  },
  campaignAccount = false
): Promise<DefinePasswordResponse> {
  return landingApiRequest<DefinePasswordResponse>(
    campaignAccount
      ? '/api/donor/campaign-account/phone-access/define-password'
      : '/api/donor/phone-access/define-password',
    {
      method: 'POST',
      body: JSON.stringify(input),
    }
  )
}

export async function landingFetchDonorMe(
  token: string,
  campaignId?: number
): Promise<LandingDonor> {
  const context = campaignId ? `?campaign_id=${campaignId}` : ''
  const response = await landingApiRequest<DonorMeResponse>(`/api/donor/me${context}`, {
    method: 'GET',
    token,
  })

  return response.donor
}

export async function landingDonorLogout(): Promise<void> {
  await landingApiRequest('/api/donor/logout', { method: 'POST' })
}

export async function landingUpdateDonorProfile(
  token: string,
  input: LandingDonorProfileUpdateInput
): Promise<LandingDonor> {
  const response = await landingApiRequest<DonorProfileResponse>('/api/donor/profile', {
    method: 'PUT',
    body: JSON.stringify(input),
    token,
  })

  return response.donor
}

export async function landingChangeDonorPassword(
  token: string,
  input: LandingDonorPasswordChangeInput
): Promise<string> {
  const response = await landingApiRequest<ChangePasswordResponse>(
    '/api/donor/auth/password',
    {
      method: 'PUT',
      body: JSON.stringify(input),
      token,
    }
  )

  return response.message
}

export async function landingFetchCampaignLuckyNumbers(
  token: string,
  campaignId: number
): Promise<LandingLuckyNumber[]> {
  const response = await landingApiRequest<CampaignLuckyNumbersResponse>(
    `/api/me/campaigns/${campaignId}/lucky-numbers`,
    {
      method: 'GET',
      token,
    }
  )

  return response.numbers ?? []
}

export type LandingDonorPhoneLookupSummary = {
  display_name: string | null
  email_masked: string | null
  document_cpf_masked: string | null
}

type DonorPhoneCheckResponse = {
  exists: boolean
  status: string
  phone: string
  donor_summary: LandingDonorPhoneLookupSummary | null
}

type DonorForgotPasswordByPhoneInput = {
  institution_id?: number
  phone: string
}

type DonorResetPasswordInput = {
  donor_id?: number
  institution_id?: number
  email?: string
  token: string
  password: string
  password_confirmation: string
}

export async function landingCheckDonorPhoneLookup(
  input: LandingDonorPhoneAccessInput
): Promise<DonorPhoneCheckResponse> {
  return landingApiRequest<DonorPhoneCheckResponse>('/api/donor/check-phone', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function landingDonorForgotPasswordByPhone(
  input: DonorForgotPasswordByPhoneInput
): Promise<string> {
  const response = await landingApiRequest<{ message: string }>(
    '/api/donor/password/forgot-by-phone',
    {
      method: 'POST',
      body: JSON.stringify(input),
    }
  )

  return (
    response.message ??
    'Se houver uma conta com este telefone e e-mail cadastrado, enviaremos um link para redefinir a senha.'
  )
}

export async function landingDonorResetPassword(
  input: DonorResetPasswordInput
): Promise<string> {
  const response = await landingApiRequest<{ message: string }>(
    '/api/donor/password/reset',
    {
      method: 'POST',
      body: JSON.stringify(input),
    }
  )

  return response.message ?? 'Senha redefinida com sucesso.'
}
